import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {DashboardComponent} from './dashboard.component';
import {AuthGuard} from '../security/auth.guard';
import {UsersComponent} from '../users/ioh-users/users.component';
import {AgenciesComponent} from '../agencies/agencies.component';
import {PreferencesComponent} from '../preferences/preferences.component';
import {AuthChildrenGuard} from "../security/auth.children.guard";
import {IoInsightAccessGuard} from "../security/io-insight-access.guard";
import {
  adminRoles,
  AllExceptPatientAllowedRoles,
  OnlyAdminAllowedRoles, OnlyAdminAndCompanyAdminAllowedRoles, OnlyAllowedExceptClinicianRoles,
  OnlyCaseManagerClinicianAllowedRoles,
  OnlyClinicianAllowedRoles,
  OnlySuperAdminAllowedRoles,
  RoleID, superAndCaseMangerRoles
} from "../common/Constants";
import {NotAllowedComponent} from "../core/not-allowed/not-allowed.component";
import {EsocComponent} from '../e-soc/e-soc/esoc.component';
import {PatientComponent} from "../patient/patient.component";
import {SupportComponent} from "../support/support.component";
import {AddSupportTicketComponent} from '../add-support-ticket/add-support-ticket.component';
import {NonIohUsersComponent} from "../users/non-ioh-users/non-ioh-users.component";
import {UnmatchedVisitsComponent} from "../unmatched-visits/unmatched-visits.component";
import {VisitComponent} from "../visit/visit.component";
import {EpisodeComponent} from "../episode/episode.component";
import {InvoicesComponent} from "../e-soc/invoices/invoices.component";
import {OthersComponent} from "../e-soc/others/others.component";
import { CompaniesComponent } from '../companies/companies.component';
import {FileUploadComponent} from "../file-upload/file-upload.component";
import { FormDataComponent } from '../form-data/form-data.component';
import { ReportsComponent } from '../reports/reports.component';
import { AppLinkComponent } from '../app-link/app-link.component';
import { LayoutComponent } from '../layout/layout.component';
import { RulesComponent } from '../rules/rules.component';
import { AdminDashboardComponent } from './admin-dashboard/admin-dashboard.component';
import { IoInsightComponent } from '../io-insight/io-insight.component';
import { AiComposerComponent } from '../ai-composer/ai-composer.component';

const routes: Routes = [
  {
    path: '',
    component: DashboardComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'ioh-users',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        component: UsersComponent
      },
      {
        path: 'users',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        component: NonIohUsersComponent
      },
      {
        path: 'e-docs',
        canActivate: [AuthChildrenGuard],
        data: { roles: superAndCaseMangerRoles },
        component: EsocComponent,
        children: [{
            path: 'invoices',
            canActivate: [AuthChildrenGuard],
            data: { roles: OnlyClinicianAllowedRoles },
            component: InvoicesComponent
          },
          {
          path: 'others',
          canActivate: [AuthChildrenGuard],
          data: { roles: OnlyClinicianAllowedRoles },
          component: OthersComponent
        },
        ]
      },
      {
        path: 'agencies',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        component: AgenciesComponent
      },
      {
        path: 'agencies/:id',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        component: AgenciesComponent
      },
      {
        path: 'support',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        component: SupportComponent
      },
      {
        path: 'support-ticket',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyCaseManagerClinicianAllowedRoles },
        component: AddSupportTicketComponent
      },
      {
        path: 'upload-file',
        canActivate: [AuthChildrenGuard],
        data: { roles: adminRoles },
        component: FileUploadComponent
      },

      {
        path: 'not-allowed',
        component:NotAllowedComponent
      },
      {
        path: 'preferences',
        canActivate: [AuthChildrenGuard],
        component: PreferencesComponent
      },
      {
        path: 'episodes',
        canActivate: [AuthChildrenGuard],
        component: EpisodeComponent
      },
      {
        path: 'visits',
        canActivate: [AuthChildrenGuard],
        component: VisitComponent
      },
      /*{
        path: 'episodes',
        canActivate: [AuthChildrenGuard],
        component: EpisodeComponent
      },*/
      {
        path: 'unmatched-visits',
        canActivate: [AuthChildrenGuard],
        component: UnmatchedVisitsComponent
      },
      {
        path: 'p-visits',
        canActivate: [AuthChildrenGuard],
        component: PatientComponent
      },
      {
        path: 'companies',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        component: CompaniesComponent
      },
      {
        path: 'form-data',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        component: FormDataComponent
      },
      {
        path: 'ioh-medication-alerts',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        loadComponent: () =>
          import('../medication-alerts/medication-alerts.component').then(
            (m) => m.MedicationAlertsComponent,
          ),
      },
      {
        path: 'ai-composer',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAndCompanyAdminAllowedRoles },
        component: AiComposerComponent,
      },
      {
        path: 'app-link',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        component: AppLinkComponent,
      },
      {
        path: 'ioh-reports',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        component: ReportsComponent
      },
      {
        path: 'layout',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        component: LayoutComponent
      },
      {
        path: 'rules',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        component: RulesComponent
      },
      {
        // IOHB-NA: the Admin Dashboard is now a two-tab shell - Logship (landing) and
        // Platform. Platform holds the previous QuickSight dashboard behind a lazy tab so its
        // embed is not paid for unless opened. See AdminDashboardComponent.
        path: 'dashboard',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        component: AdminDashboardComponent
      },
      {
        path: 'io-insight',
        canActivate: [AuthChildrenGuard, IoInsightAccessGuard],
        data: { roles: OnlyAdminAndCompanyAdminAllowedRoles },
        component: IoInsightComponent
      },
      {
        // IOHB-NA: io IQ feature route (previously "Quality Assurance").
        // Lazy-loaded as a standalone component (matches the medication-alerts
        // pattern). Visibility is gated upstream by
        // `Agency.portalFeatures.qaScrubberEnabled` in dashboard.component.ts
        // — the menu item only renders when the flag is set, so the route is
        // unreachable for users who shouldn't see it.
        path: 'quality-assurance',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        loadComponent: () =>
          import('../quality-assurance/quality-assurance.component').then(
            (m) => m.QualityAssuranceComponent,
          ),
      },
      {
        // io IQ v4.1 feature route
        path: 'quality-assurance-v4',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlyAdminAllowedRoles },
        loadComponent: () =>
          import('../quality-assurance-v4/quality-assurance-v4.component').then(
            (m) => m.QualityAssuranceV4Component,
          ),
      },
      {
        // IOHB-1737 — edit the configs the mobile app fetches at runtime.
        // `data.roles` must stay identical to the menu item's allowedRoleIds in
        // dashboard.component.ts: AuthChildrenGuard treats a route with no
        // roles as open, so a mismatch either hides a reachable page or leaves
        // the page reachable by typing the URL.
        path: 'app-config',
        canActivate: [AuthChildrenGuard],
        data: { roles: OnlySuperAdminAllowedRoles },
        loadComponent: () =>
          import('../app-config/app-config.component').then((m) => m.AppConfigComponent),
      }
    ]
  },
  // {
  //   path: 'esoc-verification',
  //   //canActivate: [AuthChildrenGuard],
  //  // data: { roles: OnlyAdminAllowedRoles },
  //   component: EsocVerificationComponent
  // },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class DashboardRoutingModule { }
